import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import AvatarMedium from '../../../../components/Avatar/Avatar';
import BulletPointsFormatter from '../../../../util/formatters/bulletPointsFormatter/bulletPointsFormatter';
import Modal from '../Utils/Modal/Modal';
import { ShieldCheckIcon } from '../icons';
import { getListingReviewStats } from '../reviewStats';
import css from './AboutHost.module.css';

const AboutHost = ({ currentListing, currentLanguage = 'de', reviews = [] }) => {
  const intl = useIntl();
  const [projectPic, setProjectPic] = useState('');
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);

  const host = currentListing?.author;
  const hostProfile = host?.attributes?.profile?.publicData || {};
  const publicData = currentListing?.attributes?.publicData || {};

  useEffect(() => {
    if (currentListing?.images?.length > 0) {
      const last = currentListing.images[currentListing.images.length - 1];
      const lastUrl =
        last?.attributes?.variants?.['scaled-medium']?.url ||
        last?.attributes?.variants?.['landscape-crop2x']?.url ||
        last?.attributes?.variants?.['landscape-crop']?.url ||
        '';
      setProjectPic(lastUrl);
    }
  }, [currentListing]);

  const fullName = host?.attributes?.profile?.displayName || '';
  const firstName = fullName ? fullName.split(' ')[0] : '';

  const partnerYears = useMemo(() => {
    const createdAt = host?.attributes?.createdAt;
    if (!createdAt) return null;
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return null;
    return Math.max(1, Math.round((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24 * 365)));
  }, [host]);

  const hostAboutText =
    currentLanguage === 'en'
      ? hostProfile.aboutTheHost ||
        intl.formatMessage({ id: 'AboutProjectNew.placeholderHost' }, { hostName: firstName })
      : hostProfile.aboutTheHost_de ||
        hostProfile.aboutTheHost ||
        intl.formatMessage({ id: 'AboutProjectNew.placeholderHost' }, { hostName: firstName });

  const projectDescription =
    currentLanguage === 'en'
      ? hostProfile?.projectDescription || ''
      : hostProfile?.projectDescription_de || hostProfile?.projectDescription || '';

  const preview250 = useMemo(() => {
    if (projectDescription.length <= 250) return projectDescription;
    let cut = 250;
    while (cut < projectDescription.length && projectDescription[cut] !== ' ') cut += 1;
    return `${projectDescription.slice(0, cut).trim()}…`;
  }, [projectDescription]);

  const { count: reviewsCount, rating: ratingValue } = getListingReviewStats(
    currentListing,
    reviews
  );
  const formattedRating = intl.formatNumber(ratingValue, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  if (!host && !projectDescription && !hostAboutText) {
    return null;
  }

  return (
    <section
      id="aboutProjectComponent"
      className={css.section}
      data-elbaction="visible:vis projecttext"
    >
      <h2 className={css.title}>
        <FormattedMessage id="AboutProjectNew.heading" />
      </h2>

      <div className={css.card}>
        <div className={css.content}>
          <div className={css.hostLayout}>
            <div className={css.hostProfile}>
              <div className={css.hostAvatarWrap}>
                <AvatarMedium className={css.hostAvatar} user={host} disableProfileLink />
                <span className={css.verifiedBadge}>
                  <ShieldCheckIcon className={css.verifiedBadgeSvg} size={15} strokeWidth={2} />
                </span>
              </div>

              <div className={css.hostProfileText}>
                <h3 className={css.hostName}>{firstName || fullName || 'Host'}</h3>
                <div className={css.verifiedInline}>
                  <ShieldCheckIcon className={css.verifiedInlineSvg} size={12} strokeWidth={2} />
                  <FormattedMessage id="AboutHost.verifiedHost" />
                </div>
              </div>
            </div>

            <div className={css.hostDetails}>
              <div className={css.verifiedPill}>
                <ShieldCheckIcon className={css.verifiedPillSvg} size={14} strokeWidth={2} />
                <FormattedMessage id="AboutHost.verifiedHost" />
              </div>

              <p className={css.hostQuote}>„{hostAboutText}"</p>

              {projectDescription && (
                <div className={css.projectText}>
                  <BulletPointsFormatter text={preview250} />
                </div>
              )}

              {projectDescription.length > 250 && (
                <>
                  <button
                    type="button"
                    className={css.readMoreBtn}
                    onClick={() => setIsProjectModalOpen(true)}
                  >
                    <FormattedMessage id="EditListingOffersForm.activities.showMore" />
                  </button>
                  {isProjectModalOpen && (
                    <Modal isOpen={isProjectModalOpen} onClose={() => setIsProjectModalOpen(false)}>
                      <h2>
                        <FormattedMessage id="AboutProjectNew.heading" />
                      </h2>
                      <div style={{ whiteSpace: 'pre-wrap', fontSize: 'medium' }}>
                        <BulletPointsFormatter text={projectDescription} />
                      </div>
                    </Modal>
                  )}
                </>
              )}

              <div className={css.statsRow}>
                {partnerYears && (
                  <div>
                    <div className={css.statValue}>
                      <FormattedMessage
                        id="ImpactBar.partnerYearsValue"
                        values={{ years: partnerYears }}
                      />
                    </div>
                    <div className={css.statLabel}>
                      <FormattedMessage id="ImpactBar.partnerLabel" />
                    </div>
                  </div>
                )}
                {ratingValue > 0 && reviewsCount > 0 && (
                  <div>
                    <div className={css.statValue}>
                      {formattedRating} ★ ({reviewsCount})
                    </div>
                    <div className={css.statLabel}>
                      <FormattedMessage id="AboutHost.guestRating" />
                    </div>
                  </div>
                )}
                <div>
                  <div className={css.statValue}>
                    <FormattedMessage id="AboutHost.responseTimeValue" />
                  </div>
                  <div className={css.statLabel}>
                    <FormattedMessage id="AboutHost.responseTime" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutHost;
